/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isValid } from 'date-fns';
import { parseDate } from '@airasia-rn/ui';

/**
 * Formats a date to match the UI requirement: "Fri, 25 Feb"
 * Creates a UTC date at noon to prevent timezone issues with parseDate
 */
export const formatDateLabel = (date: Date | undefined): string => {
  if (!date || !isValid(date)) return '';
  // Create a UTC date at noon to prevent timezone shifts
  // parseDate().formatDateWithDay() uses getUTCMonth(), so we need UTC-based date
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0),
  );
  return parseDate(utcDate).formatDateWithDay();
};

/** Separator between the two dates of the combined field, as used by flights. */
const DATE_RANGE_SEPARATOR = '-';

/**
 * Matches the zero of a zero-padded day in the latin date layouts
 * ("Fri, 07 Aug", "ศ. 07 ส.ค.", "Th 6, 07 thg 8"). CJK layouts render the day as
 * "07日" and are intentionally left padded, which is their normal form.
 */
const PADDED_DAY_PATTERN = /([,.]\s)0(\d\s)/;

/**
 * Formats a date pair for the combined single date field:
 * "Fri, 7 Aug - Mon, 10 Aug", or "Fri, 7 Aug" when there is no second date.
 *
 * Built on formatDateLabel so every locale keeps its own layout; only the day's
 * leading zero is dropped, to match the flights travel-date field. Callers that
 * still render two separate fields keep using formatDateLabel directly.
 */
export const formatDateRangeLabel = (
  fromDate: Date | undefined,
  toDate: Date | undefined,
): string => {
  const from = formatDateLabel(fromDate).replace(PADDED_DAY_PATTERN, '$1$2');
  if (!from) return '';

  const to = formatDateLabel(toDate).replace(PADDED_DAY_PATTERN, '$1$2');
  return to ? `${from} ${DATE_RANGE_SEPARATOR} ${to}` : from;
};
